import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import FilterOptionsButton from "@/components/layouts/FilterOptionsButton";
import PageLoader from "@/components/loader/PageLoader";
import ProductSelectionModal from "@/components/modals/ProductSelectionModal";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface UpsellProps {
  onMenuItemClick: (
    componentName: string,
    successMessage?: string,
    selectedItemId?: number,
  ) => void;
  successMessage: string;
}

interface FilterState {
  status: string;
  category: string;
  priceGt: string;
  priceLt: string;
}

interface ProductItem {
  id: number;
  name: string;
  unit_price: string;
  category: string;
  category_name: string;
  created_at: string;
  image: string;
  is_upsell: boolean;
  menu_display_name: string;
  stock: boolean;
}

interface Product {
  id: number;
  name: string;
  menu_display_name: string;
}

interface PendingChange {
  type: "delete";
  productId: number;
}

const Upsell = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [upsellProducts, setUpsellProducts] = useState<ProductItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [axiosInstance, isFetching] = useAxiosInstance();
  const [filterState, setFilterState] = useState<FilterState>({
    status: "",
    category: "",
    priceGt: "",
    priceLt: "",
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Keep track of pending changes
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  // Keep a copy of the original products for restoration if needed
  const [originalProducts, setOriginalProducts] = useState<ProductItem[]>([]);

  const fetchUpsellProducts = useCallback(async () => {
    try {
      if (axiosInstance && user?.selectedBranch) {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
        let url = `${apiUrl}?branch=${
          user.selectedBranch.id
        }&is_upsell=true&page=${currentPage}&page_size=10&menu_display_name=${encodeURIComponent(
          searchTerm,
        )}`;
        if (filterState.status) {
          url += `&status=${filterState.status}`;
        }
        if (filterState.category) {
          url += `&category=${encodeURIComponent(filterState.category)}`;
        }
        if (filterState.priceGt) {
          url += `&unit_price__gt=${filterState.priceGt}`;
        }
        if (filterState.priceLt) {
          url += `&unit_price__lt=${filterState.priceLt}`;
        }

        const response = await axiosInstance.get(url);
        const products = response.data.results;
        setUpsellProducts(products);
        setOriginalProducts(products); // Store original state
        setTotalPages(Math.ceil(response.data.count / 10));
      }
    } catch (error) {
      console.error("Error fetching upsell products:", error);
    }
  }, [axiosInstance, user, currentPage, searchTerm, filterState]);

  useEffect(() => {
    fetchUpsellProducts();
  }, [fetchUpsellProducts]);

  const handleAddProduct = () => {
    setShowProductSelection(true);
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleRemoveFilters = () => {
    setFilterState({
      status: "",
      category: "",
      priceGt: "",
      priceLt: "",
    });
    setCurrentPage(1);
    fetchUpsellProducts();
  };

  useEffect(() => {
    const fetchData = async () => {
      setCurrentPage(1);
      await fetchUpsellProducts();
    };

    fetchData();
  }, [filterState]);

  const handleSaveProducts = async (selectedProductIds: number[]) => {
    try {
      for (const productId of selectedProductIds) {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
        await axiosInstance.patch(`${apiUrl}${productId}/`, {
          is_upsell: true,
        });
      }
      await fetchUpsellProducts();
      setIsDirty(false);
      setPendingChanges([]); // Clear pending changes after successful save
      toast.success("Products saved successfully.");
    } catch (error) {
      console.error("Error saving upsell products:", error);
      toast.error("Failed to save products.");
    }
  };

  const handleProductSave = (selectedProducts: Product[]) => {
    const selectedProductIds = selectedProducts.map((product) => product.id);
    handleSaveProducts(selectedProductIds);
  };

  const handleDeleteProduct = (productId: number) => {
    // Add to pending changes
    setPendingChanges((prev) => [...prev, { type: "delete", productId }]);
    // Remove from current display
    setUpsellProducts((prev) =>
      prev.filter((product) => product.id !== productId),
    );
    // Set dirty state
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Process all pending changes
      for (const change of pendingChanges) {
        if (change.type === "delete") {
          const apiUrl =
            import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
          await axiosInstance.patch(`${apiUrl}${change.productId}/`, {
            is_upsell: false,
          });
        }
      }
      // Clear pending changes and refresh products
      setPendingChanges([]);
      await fetchUpsellProducts();
      setIsDirty(false);
      toast.success("Changes saved successfully.");
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    // Restore original products
    setUpsellProducts(originalProducts);
    // Clear pending changes
    setPendingChanges([]);
    setIsDirty(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <ContextualSaveBar
        isDirty={isDirty}
        onSave={handleSave}
        onDiscard={handleDiscard}
        isSaving={isSaving}
      />
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold">{t("Upsell Product")}</h1>
          <button
            onClick={handleAddProduct}
            className="flex items-center rounded bg-black px-4 py-2 text-white"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {t("Add Product")}
          </button>
        </div>
        <div className="mb-4 flex items-center justify-between">
          <input
            type="text"
            placeholder={t("Search product...")}
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full rounded border px-4 py-2 md:w-1/2"
          />
          <div className="relative">
            <FilterOptionsButton
              filterOptions={[
                {
                  label: "Category",
                  value: filterState.category,
                  onChange: (value) => handleFilterChange("category", value),
                  placeholder: t("Enter Category"),
                  type: "input",
                },
                {
                  label: "Price greater than",
                  value: filterState.priceGt,
                  onChange: (value) => handleFilterChange("priceGt", value),
                  placeholder: "Enter Price",
                  type: "input",
                },
                {
                  label: "Price less than",
                  value: filterState.priceLt,
                  onChange: (value) => handleFilterChange("priceLt", value),
                  placeholder: "Enter Price",
                  type: "input",
                },
              ]}
              onRemoveFilters={handleRemoveFilters}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {upsellProducts.map((product) => (
            <div
              key={product.id}
              className="relative flex rounded-lg bg-white shadow transition-shadow hover:shadow-lg"
            >
              {/* Status Bar */}
              <div
                className={`absolute left-0 top-0 h-full w-1.5 rounded-l-lg ${
                  product.stock ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <div className="flex w-full items-center p-4 pl-6">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-200">
                        <span className="text-gray-400">No image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <h3 className="font-bold text-gray-900">
                      {product.menu_display_name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {product.category_name}
                    </p>
                    <p className="mt-1 font-bold text-gray-900">
                      ${parseFloat(product.unit_price).toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="ml-auto">
                  <button
                    onClick={() => handleDeleteProduct(product.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-50"
                    title={t("DELETE")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <nav className="inline-flex">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index + 1)}
                className={`border px-3 py-1 ${
                  index + 1 === currentPage
                    ? "bg-red-500 text-white"
                    : "bg-white text-black hover:bg-gray-50"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </nav>
        </div>
        <ProductSelectionModal
          isOpen={showProductSelection}
          onClose={() => setShowProductSelection(false)}
          onSave={handleProductSave}
          initialSelectedProducts={upsellProducts.map((p) => p.id)}
        />
      </div>
    </PageLoader>
  );
};

export default Upsell;
